import {
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  OnInit,
  Optional,
  Output,
  Self,
} from '@angular/core';
import { NgControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ControlsOf, FormBuilder, FormGroup } from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  take,
  takeUntil,
} from 'rxjs/operators';

import { LoadingService } from '@digital-platform/shared/services';
import {
  FormValidators,
  NestedFormComponent,
} from '@digital-platform/shared/ui';
import {
  CreateAccount,
  CreateAccountModel,
  PasswordRequirements,
} from '@digital-platform/users/domain';
import { UserInviteService } from '@digital-platform/users/services';
import { PASSWORD_REQUIREMENTS } from '../users-ui.module';

@UntilDestroy()
@Component({
  selector: 'digital-platform-create-account',
  templateUrl: './create-account.component.html',
  styleUrls: ['./create-account.component.scss'],
})
export class CreateAccountComponent
  extends NestedFormComponent<CreateAccount>
  implements OnInit
{
  @HostBinding() class = 'digital-platform-create-account';

  public form: FormGroup<ControlsOf<CreateAccount>>;
  public showFeedback = false;
  public showPassword = false;
  public disableEmail = false;
  public disableInviteCode = false;
  public disableCompanyName = false;
  public checkInviteCode = true;

  @Output() public createAccountData = new EventEmitter<CreateAccount>();
  @Output() public formStatus = new EventEmitter<any>();

  constructor(
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private userInviteService: UserInviteService,
    private loadingService: LoadingService,
    private route: ActivatedRoute,
    @Inject(PASSWORD_REQUIREMENTS) private requirements: PasswordRequirements,
    @Optional() @Self() public controlDir: NgControl
  ) {
    super(controlDir);
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        take(1),
        filter((params) => !!params.inviteId)
      )
      .subscribe((params) =>
        this.userInviteService.getInviteFromLink(params.inviteId)
      );

    this.userInviteService.companyName$
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((companyName) => {
        console.log(companyName);
        this.form.controls.companyName.setValue(companyName);
        this.form.controls.companyName.disable();
        this.checkInviteCode = false;
      });

    this.userInviteService.email$
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((email) => {
        this.form.controls.email.setValue(email);
        this.form.controls.email.disable();
      });

    this.userInviteService.inviteCode$
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((inviteCode) => {
        this.form.controls.inviteCode.setValue(
          inviteCode.replace(/^(\w{0,4})(\w{0,4})/, '$1-$2')
        );
        this.form.controls.inviteCode.disable();
      });
    let value = CreateAccountModel.DEFAULT_CONFIG;

    if (this.value) {
      value = this.value;
    }

    if (!this.form) {
      this.buildForm(value);
    } else {
      this.form.patchValue(value);
    }

    this.watchFormChanges();

    super.ngOnInit();
  }

  private buildForm(value: CreateAccount) {
    this.form = this.formBuilder.group({
      firstName: [value.firstName, Validators.required],
      lastName: [value.lastName, Validators.required],
      title: [value.title, Validators.required],
      companyName: [
        value.companyName,
        [Validators.required, FormValidators.specialCharacters],
      ],
      inviteCode: [
        value.inviteCode,
        [Validators.maxLength(9), Validators.minLength(9)],
      ],
      email: [value.email, [Validators.required, Validators.email]],
      phoneNumber: [
        value.phoneNumber,
        [
          Validators.required,
          Validators.pattern(
            /^[(]{0,1}[0-9]{3}[)]{0,1}[-\s.]{0,1}[0-9]{3}[-\s.]{0,1}[0-9]{4}/
          ),
        ],
      ],
      password: [
        value.password,
        [
          Validators.required,
          FormValidators.passwordRequirements(this.requirements),
        ],
      ],
      agreeTermsOfService: [value.agreeTermsOfService, Validators.requiredTrue],
    });
  }

  public errorHandling = (control: string, error: string) =>
    this.form.controls[control].touched &&
    this.form.controls[control].hasError(error);

  watchFormChanges() {
    this.form.value$
      .pipe(takeUntil(this.destroyed$), debounceTime(1000))
      .subscribe(async () => {
        const shouldDoInviteCodeLookup = await this.shouldDoInviteCodeLookup();

        if (shouldDoInviteCodeLookup) {
          this.userInviteService.getInviteFromCode(
            this.form.controls.email.value,
            this.form.controls.inviteCode.value.replace('-', '')
          );
        }
      });

    this.form.value$.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
      this.showFeedback = this.showFeedback || !!value.password; // shows feedback if we have a value
      this.createAccountData.emit(value);
      this.formStatus.emit(this.form);
    });
  }

  private async shouldDoInviteCodeLookup(): Promise<boolean> {
    let doLookup = true;
    const loading = await this.loadingService.loading$
      .pipe(take(1))
      .toPromise();
    if (this.form.controls.inviteCode.invalid) {
      doLookup = false;
    }

    if (this.form.controls.email.invalid) {
      doLookup = false;
    }

    if (!this.checkInviteCode) {
      doLookup = false;
    }

    if (loading) {
      doLookup = false;
    }

    return doLookup;
  }
}
